import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers/display-toast';
import { invalidateOnboardingProjectValidationReport } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';

import { upsertProjectsBulk } from '../api/projects';
import { UpsertProjectsBulkRequest } from '../types/request/upsert-project-bulk.request';

export function useUpsertProjects({
  onSuccess,
}: { onSuccess?: (addedCount: number) => void } = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertProjectsBulkRequest) => upsertProjectsBulk(data),
    onSuccess: async (_, variables) => {
      const projects = variables.items;

      if (projects.length === 1) {
        displayToastSuccess(
          t('authenticated.onboarding.projects:addModal.projectAdded', { name: projects[0].name })
        );
      } else {
        displayToastSuccess(
          t('authenticated.onboarding.projects:addModal.projectsAdded', { total: projects.length })
        );
      }
      await invalidateOnboardingProjectValidationReport(queryClient);

      onSuccess?.(projects.length);
    },
    onError: (error: Error) => {
      displayToastError(error.message);
    },
  });
}
